import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, ArrowLeft, Play, Share2, RefreshCw, Trash2, Check, Copy, MapPin, Loader2, Smartphone, Pause, Ban, Flag, BookOpen, Camera, Bell, Navigation, Settings } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import TripLiveView from "@/components/TripLiveView";
import TripJournalPanel from "@/components/trip/TripJournalPanel";
import TripPhotosPanel from "@/components/trip/TripPhotosPanel";
import TripNotifyPanel from "@/components/trip/TripNotifyPanel";
import TripSettingsPanel from "@/components/trip/TripSettingsPanel";
import TripRouteEditor from "@/components/trip/TripRouteEditor";

const EVENT_LABEL = { passed_near: "passed near", arrived: "arrived", stopped: "stopped" };

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const blank = { name: "", lat: "", lon: "", kind: "city" };

const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };

function WaypointFields({ label, value, onChange }) {
  const v = value || { name: "", lat: "", lon: "" };
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="grid grid-cols-3 gap-2 items-end">
      <div className="col-span-3 text-xs font-medium text-charcoal-500">{label}</div>
      <input data-testid={`wp-${label}-name`} className="border rounded-lg px-2 py-1.5 text-sm" placeholder="City / place" value={v.name} onChange={(e) => set("name", e.target.value)} />
      <input className="border rounded-lg px-2 py-1.5 text-sm" placeholder="lat" value={v.lat} onChange={(e) => set("lat", e.target.value)} />
      <input className="border rounded-lg px-2 py-1.5 text-sm" placeholder="lon" value={v.lon} onChange={(e) => set("lon", e.target.value)} />
    </div>
  );
}

function CitiesEditor({ cities, setCities }) {
  const upd = (i, k, val) => setCities(cities.map((c, j) => (j === i ? { ...c, [k]: val } : c)));
  return (
    <div className="space-y-2" data-testid="cities-editor">
      <div className="text-xs font-medium text-charcoal-500">Route cities & crossings (in order)</div>
      {cities.map((c, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <input className="col-span-5 border rounded-lg px-2 py-1.5 text-sm" placeholder="City / bridge" value={c.name} onChange={(e) => upd(i, "name", e.target.value)} />
          <input className="col-span-2 border rounded-lg px-2 py-1.5 text-sm" placeholder="lat" value={c.lat} onChange={(e) => upd(i, "lat", e.target.value)} />
          <input className="col-span-2 border rounded-lg px-2 py-1.5 text-sm" placeholder="lon" value={c.lon} onChange={(e) => upd(i, "lon", e.target.value)} />
          <select className="col-span-2 border rounded-lg px-1 py-1.5 text-sm" value={c.kind} onChange={(e) => upd(i, "kind", e.target.value)}>
            <option value="city">city</option>
            <option value="border">border</option>
          </select>
          <button className="col-span-1 text-charcoal-400 hover:text-red-600" onClick={() => setCities(cities.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
        </div>
      ))}
      <button data-testid="add-city-btn" className="text-sm text-terracotta font-medium" onClick={() => setCities([...cities, { ...blank }])}>+ Add city / crossing</button>
    </div>
  );
}

function CreateTrip({ onDone, onCancel }) {
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [f, setF] = useState({ name: "", from_template_id: "", client_id: "", customer_name: "", pets: "", driver_id: "", driver_name: "", device_label: "van-1", departure_date: "" });
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [crossing, setCrossing] = useState(null);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/route-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/admin/drivers").then((r) => setDrivers(r.data)).catch(() => {});
  }, []);

  const applyTemplate = (id) => {
    setF({ ...f, from_template_id: id });
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setOrigin(t.origin); setDestination(t.destination); setCrossing(t.crossing);
    setCities((t.cities || []).map((c) => ({ name: c.name, lat: c.lat, lon: c.lon, kind: c.kind })));
  };

  const pickClient = (id) => {
    const c = clients.find((x) => x.id === id);
    setF({ ...f, client_id: id, customer_name: c?.name || f.customer_name, pets: (c?.pets || []).map((p) => p.name).filter(Boolean).join(", ") || f.pets });
  };

  const toWp = (w) => (w && w.name ? { name: w.name, lat: parseFloat(w.lat), lon: parseFloat(w.lon) } : null);
  const payload = () => ({
    name: f.name || null, from_template_id: f.from_template_id || null,
    client_id: f.client_id || null, customer_name: f.customer_name,
    pets: f.pets.split(",").map((s) => s.trim()).filter(Boolean).map((n) => ({ name: n })),
    driver_id: f.driver_id || null, driver_name: drivers.find((d) => d.id === f.driver_id)?.name || f.driver_name,
    device_label: f.device_label, departure_date: f.departure_date || null,
    origin: toWp(origin), destination: toWp(destination),
    crossing: crossing && crossing.name ? { name: crossing.name, direction: crossing.direction || "", lat: crossing.lat ? parseFloat(crossing.lat) : null, lon: crossing.lon ? parseFloat(crossing.lon) : null } : null,
    cities: cities.filter((c) => c.name && c.lat && c.lon).map((c, i) => ({ name: c.name, lat: parseFloat(c.lat), lon: parseFloat(c.lon), kind: c.kind, order: i })),
  });

  const save = async () => {
    if (!f.customer_name.trim()) return toast.error("Add a customer name");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/trips", payload());
      toast.success("Trip created");
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveTemplate = async () => {
    if (!f.name.trim()) return toast.error("Name the route first (used as the template name)");
    try {
      await api.post("/admin/route-templates", { name: f.name, origin: toWp(origin), destination: toWp(destination),
        crossing: crossing?.name ? { name: crossing.name, direction: crossing.direction || "", lat: crossing.lat ? parseFloat(crossing.lat) : null, lon: crossing.lon ? parseFloat(crossing.lon) : null } : null,
        cities: cities.filter((c) => c.name && c.lat && c.lon).map((c, i) => ({ name: c.name, lat: parseFloat(c.lat), lon: parseFloat(c.lon), kind: c.kind, order: i })) });
      toast.success("Saved as reusable template");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="create-trip-form">
      <button className="text-sm text-charcoal-500 flex items-center gap-1" onClick={onCancel}><ArrowLeft size={15} /> Back</button>
      <h2 className="font-serif text-2xl text-charcoal-800">Create a trip</h2>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm">Trip / route name
          <input data-testid="trip-name" className="mt-1 w-full border rounded-lg px-3 py-2" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Eagle Pass → Mérida" />
        </label>
        <label className="text-sm">Start from saved route
          <select data-testid="trip-template" className="mt-1 w-full border rounded-lg px-3 py-2" value={f.from_template_id} onChange={(e) => applyTemplate(e.target.value)}>
            <option value="">— none —</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <label className="text-sm">Customer
          <select data-testid="trip-client" className="mt-1 w-full border rounded-lg px-3 py-2" value={f.client_id} onChange={(e) => pickClient(e.target.value)}>
            <option value="">— type name below / pick —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
          </select>
        </label>
        <label className="text-sm">Customer name
          <input data-testid="trip-customer-name" className="mt-1 w-full border rounded-lg px-3 py-2" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} />
        </label>
        <label className="text-sm">Pets (comma separated)
          <input data-testid="trip-pets" className="mt-1 w-full border rounded-lg px-3 py-2" value={f.pets} onChange={(e) => setF({ ...f, pets: e.target.value })} placeholder="Luna, Max" />
        </label>
        <label className="text-sm">Driver
          <select data-testid="trip-driver" className="mt-1 w-full border rounded-lg px-3 py-2" value={f.driver_id} onChange={(e) => setF({ ...f, driver_id: e.target.value })}>
            <option value="">— unassigned —</option>
            {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
        <label className="text-sm">Tracking phone label
          <input data-testid="trip-device" className="mt-1 w-full border rounded-lg px-3 py-2" value={f.device_label} onChange={(e) => setF({ ...f, device_label: e.target.value })} />
        </label>
        <label className="text-sm">Departure date
          <input type="date" data-testid="trip-departure" className="mt-1 w-full border rounded-lg px-3 py-2" value={f.departure_date} onChange={(e) => setF({ ...f, departure_date: e.target.value })} />
        </label>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 bg-sand-50 p-3 rounded-xl">
        <WaypointFields label="Start" value={origin} onChange={setOrigin} />
        <WaypointFields label="Destination" value={destination} onChange={setDestination} />
        <WaypointFields label="Planned crossing" value={crossing} onChange={setCrossing} />
      </div>

      <CitiesEditor cities={cities} setCities={setCities} />

      <div className="flex flex-wrap gap-3 pt-2">
        <button data-testid="save-trip-btn" disabled={saving} onClick={save} className="bg-terracotta text-white rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin inline" size={15} /> : "Create trip"}
        </button>
        <button data-testid="save-template-btn" onClick={saveTemplate} className="border border-terracotta text-terracotta rounded-full px-5 py-2.5 text-sm font-medium">Save route as template</button>
      </div>
    </div>
  );
}

function DeviceSetup({ trip, token }) {
  const url = `${BACKEND}/api/track/ingest/${token || "YOUR_TOKEN"}`;
  const simUrl = `${window.location.origin}/track-sim?e=${encodeURIComponent(url)}&id=${encodeURIComponent(trip.device_label || "van-1")}`;
  return (
    <div className="rounded-xl border border-sand-200 bg-white p-4 space-y-2 text-sm" data-testid="device-setup">
      <div className="font-medium text-charcoal-800 flex items-center gap-2"><Smartphone size={16} /> Tracking phone setup</div>
      <p className="text-charcoal-500">Install the free <b>Traccar Client</b> app on the trip phone. Set <b>Device identifier</b> = <code>{trip.device_label}</code> and paste this exact <b>Server URL</b>:</p>
      {token ? (
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-sand-50 px-2 py-1.5 rounded break-all text-xs" data-testid="ingest-url">{url}</code>
          <button onClick={() => copy(url)} className="text-charcoal-500 hover:text-terracotta"><Copy size={15} /></button>
        </div>
      ) : <p className="text-charcoal-400 text-xs">Token is shown once at creation. Use “Regenerate device link” to get a new one.</p>}
      <a href={simUrl} target="_blank" rel="noreferrer" className="inline-block text-terracotta font-medium">Test from any phone/browser → open GPS simulator</a>
    </div>
  );
}

function TripDetail({ trip: initial, onBack, onChanged }) {
  const [trip, setTrip] = useState(initial);
  const [token, setToken] = useState(initial.device_token || "");
  const [stamps, setStamps] = useState([]);
  const [manual, setManual] = useState({ name: "", kind: "city" });
  const [tab, setTab] = useState("tracking");

  const loadLive = () => api.get(`/admin/trips/${trip.id}/live`).then((r) => { setStamps(r.data.stamps || []); return r.data; });
  useEffect(() => { loadLive(); /* eslint-disable-next-line */ }, [trip.id]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); const { data } = await api.get(`/admin/trips/${trip.id}`); setTrip({ ...data }); onChanged?.(); loadLive(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const shareUrl = `${window.location.origin}/track/${trip.share_token}`;

  return (
    <div className="space-y-5" data-testid="trip-detail">
      <button className="text-sm text-charcoal-500 flex items-center gap-1" onClick={onBack}><ArrowLeft size={15} /> All trips</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-2xl text-charcoal-800">{trip.name || trip.customer_name || "Trip"} <span data-testid="trip-lifecycle-pill" className={`align-middle ml-2 text-xs px-2 py-0.5 rounded-full ${trip.status === "active" ? "bg-green-100 text-green-700" : trip.status === "paused" ? "bg-amber-100 text-amber-700" : trip.status === "draft" ? "bg-sand-200 text-charcoal-600" : "bg-charcoal-100 text-charcoal-500"}`}>{trip.status}</span></h2>
        <div className="flex flex-wrap gap-2">
          {(trip.status === "draft" || trip.status === "cancelled") && <button data-testid="start-trip-btn" onClick={() => act(() => api.post(`/admin/trips/${trip.id}/start`), "Trip started")} className="bg-green-600 text-white rounded-full px-4 py-2 text-sm flex items-center gap-1"><Play size={14} /> Start</button>}
          {trip.status === "active" && <button data-testid="pause-trip-btn" onClick={() => act(() => api.post(`/admin/trips/${trip.id}/pause`), "Trip paused")} className="bg-amber-500 text-white rounded-full px-4 py-2 text-sm flex items-center gap-1"><Pause size={14} /> Pause</button>}
          {trip.status === "paused" && <button data-testid="resume-trip-btn" onClick={() => act(() => api.post(`/admin/trips/${trip.id}/resume`), "Trip resumed")} className="bg-green-600 text-white rounded-full px-4 py-2 text-sm flex items-center gap-1"><Play size={14} /> Resume</button>}
          {(trip.status === "active" || trip.status === "paused") && <button data-testid="complete-trip-btn" onClick={() => act(() => api.post(`/admin/trips/${trip.id}/complete`), "Trip completed")} className="bg-charcoal-700 text-white rounded-full px-4 py-2 text-sm flex items-center gap-1"><Flag size={14} /> Complete</button>}
          {(trip.status === "active" || trip.status === "paused" || trip.status === "draft") && <button data-testid="cancel-trip-btn" onClick={() => { if (window.confirm("Cancel this trip? Tracking stops and the share link is disabled.")) act(() => api.post(`/admin/trips/${trip.id}/cancel`), "Trip cancelled"); }} className="border border-red-300 text-red-600 rounded-full px-4 py-2 text-sm flex items-center gap-1"><Ban size={14} /> Cancel</button>}
          <button data-testid="regen-token-btn" onClick={() => act(async () => { const { data } = await api.post(`/admin/trips/${trip.id}/regenerate-device-token`); setToken(data.device_token); }, "New device link generated")} className="border rounded-full px-4 py-2 text-sm flex items-center gap-1"><RefreshCw size={14} /> Regenerate device link</button>
          <button data-testid="delete-trip-btn" onClick={async () => { if (!window.confirm("Delete this trip and ALL its location history and stamps? This cannot be undone.")) return; try { await api.delete(`/admin/trips/${trip.id}`); toast.success("Trip deleted"); onChanged?.(); onBack(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} className="border border-red-300 text-red-600 rounded-full px-4 py-2 text-sm flex items-center gap-1"><Trash2 size={14} /> Delete</button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-sand-200 overflow-x-auto" data-testid="trip-tabs">
        {[["tracking", "Tracking", Navigation], ["journal", "Journal", BookOpen], ["photos", "Photos", Camera], ["notify", "Notifications", Bell], ["settings", "Settings", Settings]].map(([key, label, Icon]) => (
          <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === key ? "border-terracotta text-terracotta" : "border-transparent text-charcoal-500 hover:text-charcoal-800"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "tracking" && (<>
      <TripRouteEditor trip={trip} onSaved={async () => { try { const { data } = await api.get(`/admin/trips/${trip.id}`); setTrip(data); } catch { /* noop */ } }} />
      <DeviceSetup trip={trip} token={token} />

      <div className="rounded-xl border border-sand-200 bg-white p-4 flex flex-wrap items-center gap-3 text-sm" data-testid="share-row">
        <Share2 size={16} className="text-terracotta" />
        <span className="font-medium text-charcoal-700">Family share link</span>
        <button data-testid="toggle-share-btn" onClick={() => act(() => api.post(`/admin/trips/${trip.id}/share`, { enabled: !trip.share_enabled }), trip.share_enabled ? "Sharing off" : "Sharing on")}
          className={`px-3 py-1 rounded-full text-xs font-medium ${trip.share_enabled ? "bg-green-100 text-green-700" : "bg-charcoal-100 text-charcoal-600"}`}>{trip.share_enabled ? "ON" : "OFF"}</button>
        {trip.share_enabled && <><code className="bg-sand-50 px-2 py-1 rounded text-xs break-all flex-1" data-testid="share-url">{shareUrl}</code><button onClick={() => copy(shareUrl)}><Copy size={14} /></button></>}
      </div>

      <div className="rounded-xl border border-sand-200 bg-white p-4 flex flex-wrap items-center gap-3 text-sm" data-testid="lock-row">
        <Smartphone size={16} className="text-terracotta" />
        <span className="font-medium text-charcoal-700">Real-device lock</span>
        <button data-testid="toggle-lock-btn" onClick={() => act(() => api.post(`/admin/trips/${trip.id}/lock`, { enabled: !trip.real_device_only }), trip.real_device_only ? "Simulator allowed again" : "Locked to real device")}
          className={`px-3 py-1 rounded-full text-xs font-medium ${trip.real_device_only ? "bg-green-100 text-green-700" : "bg-charcoal-100 text-charcoal-600"}`}>{trip.real_device_only ? "ON" : "OFF"}</button>
        <span className="text-xs text-charcoal-400">When ON, only the real phone can post — simulator points are refused.</span>
      </div>

      <TripLiveView load={loadLive} mapHeight={460} showStamps={false} />

      <div data-testid="admin-stamps">
        <h4 className="font-serif text-lg text-charcoal-800 mb-2">Stamps & milestones</h4>
        <div className="flex gap-2 mb-3">
          <input data-testid="manual-stamp-name" className="border rounded-lg px-3 py-2 text-sm flex-1" placeholder="Add a stamp manually (city or border)" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} />
          <select className="border rounded-lg px-2 text-sm" value={manual.kind} onChange={(e) => setManual({ ...manual, kind: e.target.value })}><option value="city">city</option><option value="border">border</option></select>
          <button data-testid="add-manual-stamp-btn" onClick={() => manual.name && act(() => api.post(`/admin/trips/${trip.id}/stamps`, manual), "Stamp added").then(() => setManual({ name: "", kind: "city" }))} className="bg-terracotta text-white rounded-full px-4 text-sm">Add</button>
        </div>
        <ul className="space-y-2">
          {stamps.map((s) => (
            <li key={s.id} className="flex items-center gap-3 text-sm bg-white border border-sand-200 rounded-lg px-3 py-2" data-testid={`admin-stamp-${s.id}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${s.kind === "border" ? "bg-red-600" : "bg-green-600"}`} />
              <span className="font-medium text-charcoal-800">{s.name}</span>
              <span className="text-xs text-charcoal-400">{s.source === "gps" ? "GPS" : "manual"} · {s.status}</span>
              {s.event && <span className="text-xs px-2 py-0.5 rounded-full bg-sand-100 text-charcoal-600">{EVENT_LABEL[s.event] || s.event}{s.distance_km != null ? ` · ${s.distance_km}km` : ""}</span>}
              {s.buffered && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" title="From a delayed/replayed upload — not a live arrival">delayed</span>}
              {s.border_state && <span className="text-xs px-2 py-0.5 rounded-full bg-sand-100 text-charcoal-600">{s.border_state.replace(/_/g, " ")}</span>}
              <div className="ml-auto flex gap-2">
                {s.status === "suggested" && <button data-testid={`confirm-stamp-${s.id}`} onClick={() => act(() => api.post(`/admin/trips/${trip.id}/stamps/${s.id}/confirm`), "Border crossing confirmed")} className="text-green-600 flex items-center gap-1 text-xs"><Check size={14} /> Confirm crossing</button>}
                {s.kind === "border" && s.status === "confirmed" && s.border_state !== "procedures_complete" && <button data-testid={`procedures-stamp-${s.id}`} onClick={() => act(() => api.post(`/admin/trips/${trip.id}/stamps/${s.id}/border-procedures`), "Border procedures confirmed")} className="text-terracotta flex items-center gap-1 text-xs"><Flag size={13} /> Procedures done</button>}
                {s.status !== "rejected" && s.status !== "suggested" && s.kind !== "border" && <button data-testid={`reject-stamp-${s.id}`} onClick={() => act(() => api.patch(`/admin/trips/${trip.id}/stamps/${s.id}`, { status: "rejected", note: "manual correction" }), "Marked as not visited")} className="text-charcoal-500 hover:text-amber-600 flex items-center gap-1 text-xs"><Ban size={13} /> Reject</button>}
                <button onClick={() => act(() => api.delete(`/admin/trips/${trip.id}/stamps/${s.id}`), "Removed")} className="text-charcoal-400 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
          {stamps.length === 0 && <li className="text-sm text-charcoal-500">No stamps yet.</li>}
        </ul>
      </div>
      </>)}

      {tab === "journal" && <TripJournalPanel tripId={trip.id} />}
      {tab === "photos" && <TripPhotosPanel tripId={trip.id} />}
      {tab === "notify" && <TripNotifyPanel tripId={trip.id} />}
      {tab === "settings" && <TripSettingsPanel tripId={trip.id} />}
    </div>
  );
}

export default function TripsManager() {
  const [view, setView] = useState("list");
  const [trips, setTrips] = useState([]);
  const [active, setActive] = useState(null);
  const load = () => api.get("/admin/trips").then((r) => setTrips(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (view === "create") return <CreateTrip onCancel={() => setView("list")} onDone={(t) => { load(); setActive(t); setView("detail"); }} />;
  if (view === "detail" && active) return <TripDetail trip={active} onBack={() => { setView("list"); load(); }} onChanged={load} />;

  return (
    <div className="space-y-5" data-testid="trips-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-charcoal-800">Pet trips</h2>
        <button data-testid="new-trip-btn" onClick={() => setView("create")} className="bg-terracotta text-white rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-1"><Plus size={16} /> Create trip</button>
      </div>
      <div className="grid gap-3">
        {trips.map((t) => (
          <button key={t.id} data-testid={`trip-row-${t.id}`} onClick={() => api.get(`/admin/trips/${t.id}`).then((r) => { setActive(r.data); setView("detail"); })}
            className="text-left rounded-xl border border-sand-200 bg-white p-4 hover:border-terracotta transition-colors">
            <div className="flex items-center justify-between">
              <div className="font-medium text-charcoal-800 flex items-center gap-2"><MapPin size={15} className="text-terracotta" /> {t.name || t.customer_name || "Trip"}</div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === "active" ? "bg-green-100 text-green-700" : t.status === "paused" ? "bg-amber-100 text-amber-700" : t.status === "draft" ? "bg-sand-200 text-charcoal-600" : "bg-charcoal-100 text-charcoal-500"}`}>{t.status}</span>
            </div>
            <div className="text-sm text-charcoal-500 mt-1">{t.customer_name} · {(t.pets || []).map((p) => p.name).join(", ") || "—"} {t.departure_date ? `· departs ${t.departure_date}` : ""}</div>
          </button>
        ))}
        {trips.length === 0 && <p className="text-sm text-charcoal-500">No trips yet. Create your first reusable trip.</p>}
      </div>
    </div>
  );
}
